import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { orgContext } from '@/core/api';

/**
 * ───────────────────────────────────────────────────────────────────────────
 * CLIENT STORE CONVENTIONS (this is the reference example — see CLAUDE.md §2.3)
 * ───────────────────────────────────────────────────────────────────────────
 * - Zustand holds **client/UI/session state only**. Server data lives in
 *   TanStack Query, never here (F6).
 * - One store per concern; expose selector hooks (`useSession((s) => s.x)`) so
 *   components only re-render on the slices they read.
 * - Persist **narrowly** via `partialize` — only durable, non-sensitive prefs.
 *   Never persist `user`/`role`/`isAuthenticated` (re-fetched on bootstrap;
 *   trusting them from storage is a staleness/security footgun) or tokens
 *   (those live in `tokenStorage`; the access token is deliberately in-memory).
 * - Side effects that must mirror state (e.g. the `X-Organization` header) go in
 *   the action, not in components.
 * ───────────────────────────────────────────────────────────────────────────
 */

/** Roles mirror the backend RBAC set (BE-ADR-003). Used to gate UI (F2). */
export type Role =
  | 'super_admin'
  | 'founder'
  | 'accountant'
  | 'hr'
  | 'sales_manager'
  | 'lead_gen'
  | 'employee'
  | 'guest';

export interface Organization {
  id: number;
  name: string;
  slug: string;
  base_currency: string;
  /** Logo URL (relative path served by the backend); null if unset. */
  logo: string | null;
  /** Hex color ("" if unset). Drives the `--brand` CSS var in the shell. */
  primary_color: string;
  /** Basis a new deal opens on (ADR-046); `none` = this org pays no commission. */
  default_commission_basis: 'none' | 'first_client_invoice' | 'all_paid_invoices';
  /** Org switch — when false, manual "Log time" is disabled (timer still works). */
  manual_time_logging_enabled: boolean;
  /** Stopping the timer asks what was done: may it be skipped? (BE-ADR-109) */
  timer_note_mode: TimerNoteMode;
}

/** Mirrors projects TimerNoteMode (BE-ADR-109). */
export type TimerNoteMode = 'optional' | 'required';

export interface CurrentUser {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  /** Raw role title from the backend (UserShortSerializer.role). */
  role: string;
  /** Active-org employee photo URL, falling back to the account avatar (null if none). */
  photo?: string | null;
}

/** Map a backend role title (e.g. "Super Admin") to the UI Role union. */
const ROLE_MAP: Record<string, Role> = {
  'super admin': 'super_admin',
  superadmin: 'super_admin',
  founder: 'founder',
  accountant: 'accountant',
  finance: 'accountant',
  hr: 'hr',
  'human resources': 'hr',
  'sales manager': 'sales_manager',
  sales: 'sales_manager',
  'lead gen': 'lead_gen',
  'lead generation': 'lead_gen',
  employee: 'employee',
  user: 'employee',
  guest: 'guest',
  client: 'guest',
};

export function normalizeRole(title?: string | null): Role {
  if (!title) return 'employee';
  return ROLE_MAP[title.toLowerCase().trim()] ?? 'employee';
}

interface SessionPayload {
  user: CurrentUser;
  organizations: Organization[];
  activeOrgSlug: string | null;
  role: Role;
  /** Subcontracted guest → white-label the shell (hide org switcher/branding). */
  whiteLabel: boolean;
}

interface SessionState {
  user: CurrentUser | null;
  organizations: Organization[];
  /** Persisted across reloads (see partialize) so we land in the same tenant. */
  activeOrgSlug: string | null;
  role: Role | null;
  /** True for subcontracted guests — the shell hides the org switcher/branding
   *  and shows the neutral platform logo. Derived from the user, never persisted. */
  whiteLabel: boolean;
  isAuthenticated: boolean;
  setSession: (payload: SessionPayload) => void;
  setActiveOrg: (slug: string) => void;
  /** Merge server-confirmed org changes into the store's trimmed copy, so a
   *  settings save lands without waiting for the next bootstrap. */
  patchOrganization: (id: number, changes: Partial<Organization>) => void;
  clear: () => void;
}

export const useSession = create<SessionState>()(
  persist(
    (set) => ({
      user: null,
      organizations: [],
      activeOrgSlug: null,
      role: null,
      whiteLabel: false,
      isAuthenticated: false,

      setSession: ({ user, organizations, activeOrgSlug, role, whiteLabel }) => {
        orgContext.set(activeOrgSlug); // keep the X-Organization header in sync (F1)
        set({ user, organizations, activeOrgSlug, role, whiteLabel, isAuthenticated: true });
      },

      setActiveOrg: (slug) => {
        orgContext.set(slug);
        set({ activeOrgSlug: slug });
      },

      patchOrganization: (id, changes) =>
        set((s) => ({
          organizations: s.organizations.map((o) => (o.id === id ? { ...o, ...changes } : o)),
        })),

      clear: () => {
        orgContext.set(null);
        set({
          user: null,
          organizations: [],
          activeOrgSlug: null,
          role: null,
          whiteLabel: false,
          isAuthenticated: false,
        });
      },
    }),
    {
      name: 'dev13.session',
      // Persist ONLY the active-org preference — nothing sensitive or server-owned.
      partialize: (s) => ({ activeOrgSlug: s.activeOrgSlug }),
    },
  ),
);
